<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { selectProps, type SelectOption } from 'naive-ui';
import { labelFor, type GaiaEnumLabels, type GaiaEnumValue } from './enumSelect';

/**
 * A select over a fixed set of values — a backend enum, or a status map's keys.
 *
 * What it exists for beyond `enumToSelectOptions`: a value can be in the enum
 * before it is usable. Signature types ship SES today while AES and QES stay
 * in the list so the levels remain discoverable — those are `disabledValues`,
 * and the reason shows in the label rather than being invisible.
 */
const props = defineProps({
  ...selectProps,
  /**
   * The enum object itself, e.g. `:enum-source="SignatureType"`. Numeric keys
   * are dropped, as `enumToSelectOptions` does, so a numeric enum yields its
   * string values only.
   *
   * Named `enumSource` rather than `enum` because `enum` is a reserved word and
   * cannot be destructured or referenced as `props.enum` in a template.
   */
  enumSource: {
    type: Object as PropType<Record<string, string | number>>,
    default: undefined,
  },
  /**
   * The values, in the order to show them. Takes precedence over
   * `enumSource` — for a subset, or an order the enum does not declare.
   */
  values: {
    type: Array as PropType<GaiaEnumValue[]>,
    default: undefined,
  },
  /**
   * Labels by value. Takes a status map directly, in either of the two shapes
   * this folder uses: `Record<value, string>` and `Record<value, { label }>`.
   * A value with no entry falls back to the raw value, which is what the
   * backend enums are already written to read as.
   */
  labels: {
    type: Object as PropType<GaiaEnumLabels>,
    default: undefined,
  },
  /**
   * Values that show but cannot be picked — a level the product does not
   * support yet. Prefer this over dropping them: a missing option reads as
   * "this does not exist", a disabled one as "not yet".
   */
  disabledValues: {
    type: Array as PropType<GaiaEnumValue[]>,
    default: () => [],
  },
  /**
   * Appended to a disabled option's label, so the row says why it cannot be
   * picked. Pass '' to append nothing.
   */
  disabledSuffix: {
    type: String,
    default: ' — not available',
  },
  /** Values to leave out entirely — a member that does not apply here at all. */
  exclude: {
    type: Array as PropType<GaiaEnumValue[]>,
    default: () => [],
  },
});

/** `values` wins, then the enum's own order, then the label map's key order. */
const orderedValues = computed<GaiaEnumValue[]>(() => {
  if (props.values) return props.values;
  if (props.enumSource) {
    return Object.values(props.enumSource).filter(
      (value): value is string => typeof value === 'string'
    );
  }
  return props.labels ? Object.keys(props.labels) : [];
});

const options = computed<SelectOption[]>(() =>
  orderedValues.value
    .filter(value => !props.exclude.includes(value))
    .map(value => {
      const disabled = props.disabledValues.includes(value);
      const label = labelFor(value, props.labels);
      return {
        label: disabled ? `${label}${props.disabledSuffix}` : label,
        value,
        disabled,
      };
    })
);

const selectBindings = computed<Record<string, unknown>>(() => {
  const bindings: Record<string, unknown> = { ...props };
  delete bindings.enumSource;
  delete bindings.values;
  delete bindings.labels;
  delete bindings.disabledValues;
  delete bindings.disabledSuffix;
  delete bindings.exclude;
  bindings.options = options.value;
  return bindings;
});
</script>

<template>
  <n-select v-bind="selectBindings" />
</template>
